import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const PLACEHOLDER = /\[[^\]]*\]/;
export const isPlaceholder = (s) => !String(s || '').trim() || PLACEHOLDER.test(String(s));
export const countPlaceholders = (v) => {
  if (v == null) return 0;
  if (typeof v === 'string') return (v.match(/\[[^\]]*\]/g) || []).length;
  if (Array.isArray(v)) return v.reduce((n, x) => n + countPlaceholders(x), 0);
  if (typeof v === 'object') return Object.entries(v).filter(([k]) => !['id', 'done_by', 'done_at', 'task_id'].includes(k)).reduce((n, [, x]) => n + countPlaceholders(x), 0);
  return 0;
};
export const uid = () => Math.random().toString(36).slice(2, 10);
export const fmtDe = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
export const ago = (iso) => {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'gerade eben';
  if (m < 60) return `vor ${m} Min.`;
  if (m < 1440) return `vor ${Math.round(m / 60)} Std.`;
  return fmtDe(iso);
};

const setPath = (obj, path, value) => {
  const [head, ...rest] = path.split('.');
  if (!rest.length) return { ...obj, [head]: value };
  return { ...obj, [head]: setPath(obj[head] || {}, rest.join('.'), value) };
};

// Field-level autosave: local edits are applied immediately, batched and PATCHed 700 ms after the last keystroke.
// While nothing is pending the plan is re-fetched every 8 s so the team sees each other's edits.
export function useOnsitePlan(planId) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle');
  const pending = useRef({});
  const sectionRef = useRef('');
  const timer = useRef(null);
  const busy = useRef(false);

  const load = useCallback(async () => {
    try { const r = await api.onsiteGet(planId); setPlan(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'On-Site-Plan konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const flush = useCallback(async () => {
    if (!Object.keys(pending.current).length || busy.current) return;
    const set = pending.current; pending.current = {};
    busy.current = true; setSaveState('saving');
    try {
      const r = await api.onsitePatch(planId, set, sectionRef.current);
      if (!Object.keys(pending.current).length) setPlan(r.data);
      setSaveState('saved');
    } catch (e) {
      pending.current = { ...set, ...pending.current };
      setSaveState('error');
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { busy.current = false; }
    if (Object.keys(pending.current).length) flush();
  }, [planId]);

  const setField = useCallback((path, value, section) => {
    setPlan((p) => (p ? setPath(p, path, value) : p));
    pending.current[path] = value;
    sectionRef.current = section || path.split('.')[0];
    setSaveState('dirty');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }, [flush]);

  useEffect(() => () => { clearTimeout(timer.current); if (Object.keys(pending.current).length) api.onsitePatch(planId, pending.current, sectionRef.current).catch(() => {}); }, [planId]);

  const running = plan?.prefill?.status === 'running';
  useEffect(() => {
    const iv = setInterval(() => {
      if (Object.keys(pending.current).length || busy.current) return;
      const el = document.activeElement;
      if (el && ['INPUT', 'TEXTAREA'].includes(el.tagName) && el.closest('[data-onsite-planner]')) return;
      api.onsiteGet(planId).then((r) => setPlan(r.data)).catch(() => {});
    }, running ? 3000 : 8000);
    return () => clearInterval(iv);
  }, [planId, running]);

  const run = useCallback(async (fn, okMsg) => {
    try { const r = await fn(); if (r?.data?.id) setPlan(r.data); if (okMsg) toast.success(okMsg); return r?.data; }
    catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); return null; }
  }, []);

  const check = useCallback((list, itemId, done) => {
    setPlan((p) => p ? { ...p, [list]: p[list].map((x) => (x.id === itemId ? { ...x, done } : x)) } : p);
    return run(() => api.onsiteCheck(planId, list, itemId, done));
  }, [planId, run]);

  return { plan, loading, saveState, setField, check, run, reload: load };
}
